import { useMutation, useQuery } from '@apollo/client';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ConversationStarter, ConversationStarters } from '@eluve/blocks';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Heading,
  Icon,
  Input,
  LoadingIndicator,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { MEDICAL_SPECIALTIES } from '@eluve/utils';

import {
  deleteConversationStarterDefaultMutation,
  getTenantConversationStarterDefaultsQuery,
  insertConversationStarterDefaultMutation,
  updateConversationStarterDefaultMutation,
} from './conversationStarterDefaults.operations';

type DefaultRow = {
  id: string;
  specialty: string | null;
  language: string | null;
  starters: unknown;
};

type PendingDelete = {
  id: string;
  specialty: string | null;
  label: string;
};

const SpecialtyRow: React.FC<{
  name: string;
  row: DefaultRow | undefined;
  isExpanded: boolean;
  onToggle: () => void;
  onExpand: () => void;
  onSave: (starters: ConversationStarter[]) => Promise<void>;
  onCreate: () => void;
  onDelete: () => void;
}> = ({
  name,
  row,
  isExpanded,
  onToggle,
  onExpand,
  onSave,
  onCreate,
  onDelete,
}) => {
  return (
    <div className="border-b border-border-tertiary last:border-b-0">
      <Stack
        className="px-4 py-3"
        direction="horizontal"
        align="center"
        gap={3}
      >
        <button
          type="button"
          onClick={onToggle}
          className="flex flex-1 items-center gap-3 text-left"
        >
          <Icon
            name="ChevronRight"
            size="xs"
            className={`shrink-0 text-text-supporting transition-transform ${
              isExpanded ? 'rotate-90' : ''
            }`}
          />
          <Text weight="medium" size="xs">
            {name}
          </Text>
          {row && (
            <span className="inline-block size-2 rounded-full bg-bg-positive-strong-primary" />
          )}
        </button>

        <Stack
          className="shrink-0"
          direction="horizontal"
          align="center"
          gap={2}
          fullWidth={false}
        >
          {row ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isExpanded) onExpand();
                }}
              >
                <Icon name="Pencil" size="auto" />
                Edit
              </Button>
              <Button variant="ghost" size="sm" onClick={onDelete}>
                <Icon name="Trash" size="auto" />
                Remove
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={onCreate}>
              Add override
            </Button>
          )}
        </Stack>
      </Stack>

      {isExpanded && row && (
        <div className="border-t border-border-tertiary p-4">
          <ConversationStarters
            key={row.id}
            initialStarters={(row.starters ?? []) as ConversationStarter[]}
            onSave={onSave}
          />
        </div>
      )}
    </div>
  );
};

export const TenantConversationStarterDefaultsPage: React.FC = () => {
  const { tenantId } = useParams() as { tenantId: string };
  const toast = useToast();
  const [specialtySearch, setSpecialtySearch] = useState('');
  const [expandedSpecialties, setExpandedSpecialties] = useState<Set<string>>(
    new Set(),
  );
  const [showAllSpecialties, setShowAllSpecialties] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(
    null,
  );

  const { data, loading, refetch } = useQuery(
    getTenantConversationStarterDefaultsQuery,
    { variables: { tenantId } },
  );

  const tenantDefaults = data?.tenantDefaults ?? [];

  const defaultsBySpecialty = useMemo(() => {
    const map = new Map<string | null, DefaultRow>();
    for (const row of tenantDefaults) {
      map.set(row.specialty, row as DefaultRow);
    }
    return map;
  }, [tenantDefaults]);

  const tenantWideRow = defaultsBySpecialty.get(null);
  const configuredSpecialties = MEDICAL_SPECIALTIES.filter((s) =>
    defaultsBySpecialty.has(s),
  );
  const hasAnySpecialtyOverrides = configuredSpecialties.length > 0;

  const visibleSpecialties = showAllSpecialties
    ? MEDICAL_SPECIALTIES.filter((s) =>
        s.toLowerCase().includes(specialtySearch.toLowerCase()),
      )
    : configuredSpecialties;

  const [insertDefault] = useMutation(insertConversationStarterDefaultMutation);
  const [updateDefault] = useMutation(updateConversationStarterDefaultMutation);
  const [deleteDefault] = useMutation(deleteConversationStarterDefaultMutation);

  const toggleSpecialty = (specialty: string) => {
    setExpandedSpecialties((prev) => {
      const next = new Set(prev);
      if (next.has(specialty)) {
        next.delete(specialty);
      } else {
        next.add(specialty);
      }
      return next;
    });
  };

  const handleSave =
    (row: DefaultRow) => async (updatedStarters: ConversationStarter[]) => {
      await updateDefault({
        variables: {
          id: row.id,
          starters: updatedStarters,
          specialty: row.specialty,
          language: row.language,
        },
      });
      await refetch();
      toast.success('Conversation starters updated');
    };

  const handleCreate = async (specialty: string | null) => {
    await insertDefault({
      variables: {
        tenantId,
        userId: null,
        specialty,
        language: null,
        starters: [],
      },
    });
    if (specialty) {
      setExpandedSpecialties((prev) => new Set(prev).add(specialty));
    }
    await refetch();
    toast.success(
      specialty
        ? `Override for ${specialty} created`
        : 'Tenant-wide default created',
    );
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    await deleteDefault({ variables: { id: pendingDelete.id } });
    if (pendingDelete.specialty) {
      setExpandedSpecialties((prev) => {
        const next = new Set(prev);
        next.delete(pendingDelete.specialty!);
        return next;
      });
    }
    setPendingDelete(null);
    await refetch();
    toast.success('Override removed');
  };

  const requestDelete = (
    id: string,
    specialty: string | null,
    label: string,
  ) => {
    setPendingDelete({ id, specialty, label });
  };

  if (loading && !data) {
    return (
      <Stack
        className="py-16"
        direction="horizontal"
        align="center"
        justify="center"
      >
        <LoadingIndicator />
      </Stack>
    );
  }

  return (
    <>
      <Stack gap={8} className="py-4">
        <Stack gap={1}>
          <Heading size="xl" weight="semibold">
            Conversation Starter Defaults
          </Heading>
          <Text size="xs" color="secondary">
            Configure default conversation starters for this organization. These
            override global defaults for users in this org.
          </Text>
        </Stack>

        {/* ── Tenant-wide ── */}
        <Stack gap={3}>
          <Stack
            gap={2}
            direction="horizontal"
            align="center"
            justify="between"
          >
            <Heading size="lg" weight="semibold">
              Tenant-wide
            </Heading>
            <Stack
              direction="horizontal"
              align="center"
              gap={2}
              fullWidth={false}
            >
              {tenantWideRow ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    requestDelete(tenantWideRow.id, null, 'tenant-wide')
                  }
                >
                  <Icon name="Trash" size="auto" />
                  Remove
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCreate(null)}
                >
                  Configure
                </Button>
              )}
            </Stack>
          </Stack>

          {tenantWideRow ? (
            <ConversationStarters
              key={tenantWideRow.id}
              initialStarters={
                (tenantWideRow.starters ?? []) as ConversationStarter[]
              }
              onSave={handleSave(tenantWideRow)}
            />
          ) : (
            <Text size="sm" color="tertiary">
              Not configured — users will see global defaults.
            </Text>
          )}
        </Stack>

        {/* ── Divider ── */}
        <div className="border-t border-border-tertiary" />

        {/* ── Specialty Overrides ── */}
        <Stack gap={4} align="stretch">
          <Stack
            gap={2}
            direction="horizontal"
            align="center"
            justify="between"
          >
            <Stack>
              <Heading size="lg" weight="semibold">
                Specialty Overrides
              </Heading>
              <Text size="xs" color="tertiary">
                Override starters for specific medical specialties.
              </Text>
            </Stack>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowAllSpecialties(!showAllSpecialties);
                setSpecialtySearch('');
              }}
            >
              {showAllSpecialties ? 'Show configured only' : 'Browse all'}
            </Button>
          </Stack>

          {showAllSpecialties && (
            <Input
              type="text"
              placeholder="Search specialties..."
              value={specialtySearch}
              onChange={(e) => setSpecialtySearch(e.target.value)}
            />
          )}

          {visibleSpecialties.length > 0 ? (
            <div className="overflow-hidden rounded-lg border border-border-tertiary">
              {visibleSpecialties.map((specialty) => {
                const row = defaultsBySpecialty.get(specialty);
                return (
                  <SpecialtyRow
                    key={row?.id ?? specialty}
                    name={specialty}
                    row={row}
                    isExpanded={expandedSpecialties.has(specialty)}
                    onToggle={() => toggleSpecialty(specialty)}
                    onExpand={() =>
                      setExpandedSpecialties((prev) =>
                        new Set(prev).add(specialty),
                      )
                    }
                    onSave={row ? handleSave(row) : () => Promise.resolve()}
                    onCreate={() => handleCreate(specialty)}
                    onDelete={() =>
                      row
                        ? requestDelete(row.id, specialty, specialty)
                        : undefined
                    }
                  />
                );
              })}
            </div>
          ) : showAllSpecialties && specialtySearch ? (
            <Text size="xs" color="tertiary">
              No specialties matching &ldquo;{specialtySearch}&rdquo;.
            </Text>
          ) : !hasAnySpecialtyOverrides ? (
            <Text size="xs" color="tertiary">
              No specialty overrides configured. Click &ldquo;Browse all&rdquo;
              to add one.
            </Text>
          ) : null}
        </Stack>
      </Stack>

      {/* ── Confirm delete dialog ── */}
      <AlertDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove override?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the <strong>{pendingDelete?.label}</strong>{' '}
              conversation starter override. Users in this scope will fall back
              to the next level of defaults. This may affect active users
              immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={confirmDelete}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
